const AUTH_RETURN_PATH_KEY = 'yodi-auth-return-path';

const AUTH_PATH_PREFIXES = [
  '/login',
  '/signup',
  '/forgot-password',
  '/reset-password',
  '/force-change-password',
  '/data-policy',
  '/account-status',
  '/auth-callback',
  '/auth/callback',
];

const SENSITIVE_QUERY_PARAMS = ['token', 'code', 'state', 'error'];

const stripLangPrefix = (path: string): string => path.replace(/^\/(fr|en)(?=\/|$)/, '');

const isAuthPath = (path: string): boolean => {
  const withoutLang = stripLangPrefix(path.replace(/\/$/, '') || '/');
  return AUTH_PATH_PREFIXES.includes(withoutLang);
};

const isSafeInternalPath = (path: string): boolean => {
  if (typeof path !== 'string' || path.length === 0 || path.length > 2048) {
    return false;
  }
  if (!path.startsWith('/') || path.startsWith('//')) {
    return false;
  }
  if (/^\/[a-z][a-z0-9+.-]*:/i.test(path)) {
    return false;
  }
  return true;
};

export const sanitizeReturnPath = (path: string): string | null => {
  if (!isSafeInternalPath(path)) {
    return null;
  }

  const hashless = path.split('#')[0];
  const [pathname, search = ''] = hashless.split('?');
  const normalizedPathname = pathname.replace(/\/$/, '') || '/';

  if (isAuthPath(normalizedPathname)) {
    return null;
  }

  const withoutLang = stripLangPrefix(normalizedPathname);
  if (withoutLang === '' || withoutLang === '/') {
    return null;
  }

  const searchParams = new URLSearchParams(search);
  for (const key of SENSITIVE_QUERY_PARAMS) {
    searchParams.delete(key);
  }

  const query = searchParams.toString();
  return `${normalizedPathname}${query ? `?${query}` : ''}`;
};

export const saveAuthReturnPath = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const { pathname, search } = window.location;
  const target = sanitizeReturnPath(`${pathname}${search}`);
  if (target) {
    sessionStorage.setItem(AUTH_RETURN_PATH_KEY, target);
  }
  return target;
};

export const consumeAuthReturnPath = (): string | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  const stored = sessionStorage.getItem(AUTH_RETURN_PATH_KEY);
  if (!stored) {
    return null;
  }

  sessionStorage.removeItem(AUTH_RETURN_PATH_KEY);
  return sanitizeReturnPath(stored);
};
